import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ImportDtcDto } from '../../../../../shared/models/dtc/import-dtc-dto';
import { DtcApiService } from '../../../../services/api/dtc-api.service';
import { CancelIconComponent } from '../../../icons/cancel-icon/cancel-icon.component';
import { TranslationImportReporterComponent } from '../../../common/reporter/translation-import-reporter/translation-import-reporter.component';
import { firstValueFrom } from 'rxjs';
import { CommonUtils } from '../../../../common/common-utils';
import { ServiceApiService } from '../../../../services/api/service-api.service';
import { ImportReport } from '../../../../../shared/models/report/import-report';
import { UploadFileComponent } from '../../../common/inputs/upload-file/upload-file.component';
import { nwNumberDefaultPlaceholer, nwNumberRegExValidator } from '../../../../common/nw-number-validation';
import { majorVersionDefaultPlaceholer, majorVersionRegExValidator } from '../../../../common/major-version-validation';
import { AgentActionDirective } from '../../../../directives/agent-action.directive';
import { HotToastService } from '@ngxpert/hot-toast';

@Component({
  selector: 'app-import',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    CancelIconComponent,
    TranslationImportReporterComponent,
    UploadFileComponent,
    AgentActionDirective,
  ],
  templateUrl: './dtc-import.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './dtc-import.component.scss',
})
export class ImportComponent implements OnInit {
  public form!: FormGroup;
  public nwNumber!: FormControl;
  public majorVersion!: FormControl;
  public inputData: ImportDtcDto = {} as ImportDtcDto;
  public importReport?: ImportReport;
  public formButtonLable = 'Create NW-Number';
  public nwNumberPlaceholder = nwNumberDefaultPlaceholer;
  public majorVersionPlaceholder = majorVersionDefaultPlaceholer;

  private dtcApi = inject(DtcApiService);
  private serviceApiService = inject(ServiceApiService);
  private toastr = inject(HotToastService);

  public ngOnInit(): void {
    this.buildForm();
  }

  public onExcelSelected(files: File[]): void {
    this.formButtonLable = 'Create NW-Number';
    if (files.length > 0) {
      this.inputData.excelFile = files[0];
      this.formButtonLable = 'Create NW-Number and Import Excel File';
    }
  }

  public async importExcel(): Promise<void> {
    this.inputData.nwNumber = this.form.get('nwNumber')?.value;
    this.inputData.majorVersion = this.form.get('majorVersion')?.value;

    if (!this.inputData.nwNumber || !this.inputData.majorVersion) return;

    this.importReport = await firstValueFrom(
      this.dtcApi.importDtcFromExcel(this.inputData.nwNumber, this.inputData.majorVersion, this.inputData.excelFile)
    );

    const blob = await firstValueFrom(this.serviceApiService.getImportLogfile(this.importReport.importId));
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `dtc-import-log-${timestamp}.txt`;
    const nonEmptyLogfile = CommonUtils.downloadFile(blob, filename);
    const message = nonEmptyLogfile
      ? `Import successful. Check the log file ${filename} for details.`
      : 'Import succesful';
    this.toastr.success(message);
  }

  public closeLogs(): void {
    this.importReport = undefined;
  }

  private buildForm(): void {
    this.nwNumber = new FormControl(null, [Validators.required, nwNumberRegExValidator]);
    this.majorVersion = new FormControl(null, [Validators.required, majorVersionRegExValidator]);
    this.form = new FormGroup({
      nwNumber: this.nwNumber,
      majorVersion: this.majorVersion,
    });
  }
}
